// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyImage, { LazyImageProps } from "@rpsc/MobiusOverrides/LazyImage";
import Link, { LinkPresentationProps } from "@rpsc/MobiusOverrides/Link/Link";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import parse from "html-react-parser";
import React, { FC } from "react";

const enum fields {
    heading = "heading",
    subheading = "subheading",
    linkLabel = "linkLabel",
    link = "link",
    imageAltText = "imageAltText",
    image = "image",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.linkLabel]: string;
        [fields.link]: LinkFieldValue;
        [fields.imageAltText]: string;
        [fields.image]: string;
    };
}

export interface InfoTileStyles {
    wrapper?: InjectableCss;
    infoCardLink?: LinkPresentationProps;
    heading?: TypographyProps;
    subHeading?: TypographyProps;
    imageWrapperStyles?: InjectableCss;
    textWrapperStyles?: InjectableCss;
    content?: InjectableCss;
    headingWrapper?: InjectableCss;
    image?: LazyImageProps;
}

export const infoTileStyles: InfoTileStyles = {};

export const InfoTile: FC<OwnProps> = ({ fields: { linkLabel, link, heading, imageAltText, subheading, image } }) => {
    const { url, title } = useGetLink(link);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }
        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }
            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const styles = infoTileStyles;

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.headingWrapper}>
                <StyledWrapper {...styles.imageWrapperStyles}>
                    <LazyImage src={image} altText={imageAltText} {...styles.image} />
                </StyledWrapper>
                {heading && <Typography {...styles.heading}>{parse(heading, parserOptions)}</Typography>}
            </StyledWrapper>

            <StyledWrapper {...styles.content}>
                <StyledWrapper {...styles.textWrapperStyles}>
                    {subheading && <Typography {...styles.subHeading}>{parse(subheading, parserOptions)}</Typography>}
                </StyledWrapper>
                {(linkLabel || title || url) && (
                    <Link {...styles.infoCardLink} onClick={onClick} aria-label={linkLabel || title || url}>
                        {linkLabel || title || url}
                    </Link>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const infoCard: WidgetModule = {
    component: InfoTile,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.image,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },

            {
                fieldType: "Contextual",
                name: fields.imageAltText,
                editorTemplate: "TextField",
                displayName: "Image Alt Text",
                defaultValue: "",
                tab: basicTab,
                isRequired: true,
            },
            {
                fieldType: "Contextual",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Title",
                defaultValue: "Title",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "Subheading",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.linkLabel,
                editorTemplate: "TextField",
                displayName: "Link Label",
                defaultValue: "Label",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.link,
                displayName: "Link URL",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
        ],
    },
};

export default infoCard;
